import React, { useMemo } from 'react';
import { Spinner, Typography } from '@material-tailwind/react';

const STATUS_COLORS = {
  operational: { body: '#94a3b8', roof: '#64748b', accent: '#475569' },
  in_maintenance: { body: '#fdba74', roof: '#f97316', accent: '#c2410c' },
  under_construction: { body: '#fde68a', roof: '#eab308', accent: '#a16207' },
  closed: { body: '#fca5a5', roof: '#ef4444', accent: '#b91c1c' },
};

function defaultLayout(index, total) {
  const cols = Math.min(4, Math.max(1, Math.ceil(Math.sqrt(total))));
  const col = index % cols;
  const row = Math.floor(index / cols);
  const cellW = 200;
  const cellH = 180;
  const padX = 48;
  const padY = 56;
  return {
    x: padX + col * cellW,
    y: padY + row * cellH,
    width: 140,
    height: 120,
    rotation: 0,
  };
}

function BuildingGlyph({ building, layout, selected, onSelect }) {
  const { x, y, width, height } = layout;
  const colors = STATUS_COLORS[building.status] || STATUS_COLORS.operational;
  const floors = Math.min(8, Math.max(2, Number(building.totalFloors) || 3));
  const bodyH = height * 0.62;
  const bodyW = width * 0.72;
  const depth = width * 0.18;
  const left = x + (width - bodyW - depth) / 2;
  const baseY = y + height - 18;
  const topY = baseY - bodyH;
  const storyH = bodyH / floors;

  const front = [
    [left, topY],
    [left + bodyW, topY],
    [left + bodyW, baseY],
    [left, baseY],
  ];
  const side = [
    [left + bodyW, topY],
    [left + bodyW + depth, topY - depth * 0.45],
    [left + bodyW + depth, baseY - depth * 0.45],
    [left + bodyW, baseY],
  ];
  const roof = [
    [left, topY],
    [left + depth * 0.55, topY - depth * 0.55],
    [left + bodyW + depth, topY - depth * 0.45],
    [left + bodyW, topY],
  ];

  const toPoints = (pts) => pts.map((p) => p.join(',')).join(' ');

  return (
    <g
      role="button"
      tabIndex={0}
      className="cursor-pointer"
      onClick={() => onSelect(building)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(building);
        }
      }}
    >
      {selected && (
        <rect
          x={x - 8}
          y={y - 8}
          width={width + 16}
          height={height + 16}
          rx={12}
          fill="none"
          stroke="#ea580c"
          strokeWidth={2}
          strokeDasharray="4 3"
        />
      )}
      <polygon points={toPoints(side)} fill={colors.accent} opacity={0.9} />
      <polygon points={toPoints(front)} fill={colors.body} />
      <polygon points={toPoints(roof)} fill={colors.roof} />

      {Array.from({ length: floors }).map((_, i) => {
        const wy = topY + storyH * (i + 0.28);
        const wh = storyH * 0.38;
        const windows = 3;
        return Array.from({ length: windows }).map((__, w) => {
          const wx = left + bodyW * (0.12 + w * 0.28);
          return (
            <rect
              key={`${building._id}-w-${i}-${w}`}
              x={wx}
              y={wy}
              width={bodyW * 0.14}
              height={wh}
              rx={1}
              fill="#e2e8f0"
              opacity={0.85}
            />
          );
        });
      })}

      <text
        x={x + width / 2}
        y={y + height - 2}
        textAnchor="middle"
        className="fill-slate-700"
        style={{ fontSize: 12, fontWeight: 600 }}
      >
        {building.name || 'Building'}
      </text>
      {building.buildingCode && (
        <text
          x={x + width / 2}
          y={y + 10}
          textAnchor="middle"
          className="fill-slate-400"
          style={{ fontSize: 10 }}
        >
          {building.buildingCode}
        </text>
      )}
    </g>
  );
}

/**
 * Schematic campus canvas of drawn buildings (not a geographic map).
 */
const BranchCampusCanvas = ({
  buildings = [],
  isLoading,
  selectedBuildingId,
  onSelectBuilding,
}) => {
  const layouts = useMemo(() => {
    return buildings.map((b, index) => {
      if (b.campusLayout?.x != null && b.campusLayout?.y != null) {
        return {
          x: b.campusLayout.x,
          y: b.campusLayout.y,
          width: b.campusLayout.width || 140,
          height: b.campusLayout.height || 120,
          rotation: b.campusLayout.rotation || 0,
        };
      }
      return defaultLayout(index, buildings.length);
    });
  }, [buildings]);

  const { viewW, viewH } = useMemo(() => {
    if (!layouts.length) return { viewW: 900, viewH: 420 };
    let maxX = 320;
    let maxY = 320;
    layouts.forEach((l) => {
      maxX = Math.max(maxX, l.x + l.width + 48);
      maxY = Math.max(maxY, l.y + l.height + 48);
    });
    return { viewW: maxX, viewH: maxY };
  }, [layouts]);

  const svgHeight = Math.min(520, Math.max(360, viewH));

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!buildings.length) {
    return (
      <div className="rounded-2xl border border-dashed p-10 text-center text-gray-500">
        No buildings in this branch yet.
      </div>
    );
  }

  return (
    <div className="rounded-3xl border bg-gradient-to-b from-slate-50 to-slate-100 overflow-hidden">
      <div className="overflow-auto" style={{ maxHeight: 520 }}>
        <svg
          width="100%"
          height={svgHeight}
          viewBox={`0 0 ${viewW} ${viewH}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <pattern id="campus-grid" width="24" height="24" patternUnits="userSpaceOnUse">
              <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#cbd5e1" strokeWidth="0.5" opacity="0.5" />
            </pattern>
          </defs>
          <rect x="0" y="0" width={viewW} height={viewH} fill="url(#campus-grid)" />
          {buildings.map((building, index) => (
            <BuildingGlyph
              key={building._id}
              building={building}
              layout={layouts[index]}
              selected={selectedBuildingId === building._id}
              onSelect={onSelectBuilding}
            />
          ))}
        </svg>
      </div>
      <div className="px-4 py-2 border-t bg-white/70">
        <Typography variant="small" className="text-gray-500">
          Click a building to manage it or enter its floors.
        </Typography>
      </div>
    </div>
  );
};

export default BranchCampusCanvas;
